import { convertToGeoJSON } from '../services/geojson-utils'
import { BoundingBox, ProvinceBox } from './map-detail-level.util'

/**
 * Geometry plumbing for the zoom rule: what part of the world each province occupies and
 * what part of the world the user is looking at.
 *
 * Leaflet gives the view as a `LatLngBounds` and the API gives the provinces as a topology;
 * both are turned into the plain boxes the rule works with, so the decision stays pure and
 * testable.
 */

/** The four edges of a Leaflet `LatLngBounds`, asked one by one. */
export type MapBounds = {
  getWest: () => number
  getSouth: () => number
  getEast: () => number
  getNorth: () => number
}

/** [west, south, east, north] of whatever the user is looking at. */
export const boundsToBox = (bounds: MapBounds): BoundingBox => [
  bounds.getWest(),
  bounds.getSouth(),
  bounds.getEast(),
  bounds.getNorth(),
]

/**
 * Box of any GeoJSON coordinate tree (Polygon or MultiPolygon, with or without the extra
 * nesting), or null when there is nothing to measure.
 */
export const featureBoundingBox = (coordinates: unknown): BoundingBox | null => {
  const box: BoundingBox = [Infinity, Infinity, -Infinity, -Infinity]

  const walk = (node: unknown): void => {
    if (!Array.isArray(node)) {
      return
    }

    if (typeof node[0] === 'number' && typeof node[1] === 'number') {
      const [lng, lat] = node as [number, number]
      box[0] = Math.min(box[0], lng)
      box[1] = Math.min(box[1], lat)
      box[2] = Math.max(box[2], lng)
      box[3] = Math.max(box[3], lat)
      return
    }

    node.forEach(walk)
  }

  walk(coordinates)

  return Number.isFinite(box[0]) ? box : null
}

type FeatureLike = {
  properties?: Record<string, unknown>
  geometry?: { coordinates?: unknown }
}

/**
 * One box per province, read from the geometry the map already loads (a topology or a
 * feature collection). Features without an id or without coordinates are skipped: a wrong
 * box would silently move the zoom thresholds to the wrong place.
 */
export const provinceBoxes = (data: unknown, idKey: string = 'CPRO'): ProvinceBox[] => {
  const collection = convertToGeoJSON(data as never) as { features?: unknown[] } | undefined
  const features = collection && Array.isArray(collection.features) ? collection.features : []

  return features.reduce<ProvinceBox[]>((boxes, feature) => {
    const { properties, geometry } = (feature || {}) as FeatureLike
    const rawId = properties ? properties[idKey] : undefined
    const id = typeof rawId === 'number' ? rawId : Number(rawId)
    const box = geometry ? featureBoundingBox(geometry.coordinates) : null

    if (!Number.isFinite(id) || !box) {
      return boxes
    }

    return [...boxes, { id, box }]
  }, [])
}
